import React, { useState, useEffect } from "react";
import ReactSelect from "react-select";
import { 
  listServicios, 
  createServicio, 
  updateServicio 
} from "../api/odontologia";
import {
  listUnidadMedida014,
  listTipoItem011,
  listTributo015
} from "../api/hacienda";
import { showAlertError, showAlertExito } from "../utils/alertHelper";
import Loader from "./Loader";
import { 
  FiActivity, FiPlus, FiSearch, FiEdit3, 
  FiClock, FiDollarSign, FiTag, FiHash, FiInfo
} from "react-icons/fi";

const ProcedimientosOdontologia = () => {
  const [loading, setLoading] = useState(true);
  const [procedimientos, setProcedimientos] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentId, setCurrentId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  // Catálogos
  const [unidadesMedida, setUnidadesMedida] = useState([]);
  const [tiposItem, setTiposItem] = useState([]);
  const [tributos, setTributos] = useState([]);

  // Búsqueda y Paginación
  const [buscar, setBuscar] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  
  const [form, setForm] = useState({
    sku: "",
    name: "",
    description: "",
    price: "",
    duracion_minutos: "",
    id_unidad_medida: "",
    id_type_item: "1", 
    id_tributo: "1", 
    status: 1
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await listServicios();
      setProcedimientos(res?.data || []);
    } catch (error) {
      console.error("Error al cargar procedimientos:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCatalogos = async () => {
    try {
      const [resUnits, resTypes, resTaxes] = await Promise.all([
        listUnidadMedida014(),
        listTipoItem011(),
        listTributo015()
      ]);
      setUnidadesMedida(resUnits?.data || []);
      setTiposItem(resTypes?.data || []);
      setTributos(resTaxes?.data || []);
    } catch (error) {
      console.error("Error al cargar catálogos:", error);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (showModal) {
      fetchCatalogos();
    }
  }, [showModal]);

  const handleOpenModal = (servicio = null) => {
    if (servicio) {
      setForm({ 
        sku: servicio.sku || "",
        name: servicio.name || "",
        description: servicio.description || "",
        price: servicio.price || "",
        duracion_minutos: servicio.duracion_minutos || "",
        id_unidad_medida: servicio.id_unidad_medida?.toString() || "",
        id_type_item: servicio.id_type_item?.toString() || "",
        id_tributo: servicio.id_tributo?.toString() || "",
        status: servicio.status !== undefined ? servicio.status : 1
      });
      setCurrentId(servicio.id);
      setEditMode(true);
    } else {
      setForm({ 
        sku: "",
        name: "",
        description: "",
        price: "",
        duracion_minutos: "",
        id_unidad_medida: "18",
        id_type_item: "1",
        id_tributo: "1",
        status: 1 
      });
      setEditMode(false);
    }
    setShowModal(true);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const dataToSend = {
        ...form,
        price: parseFloat(form.price) || 0,
        duracion_minutos: parseInt(form.duracion_minutos) || 0,
        id_unidad_medida: form.id_unidad_medida,
        id_type_item: form.id_type_item,
        id_tributo: form.id_tributo,
        status: parseInt(form.status)
      };

      if (editMode) {
        await updateServicio(currentId, dataToSend);
      } else {
        await createServicio(dataToSend);
      }
      setShowModal(false);
      fetchData();
    } catch (error) {
      console.error("Error al guardar el procedimiento:", error);
      showAlertError("Error", "No se pudo guardar el procedimiento.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSelectChange = (val, name) => {
    setForm(prev => ({ ...prev, [name]: val ? val.value : "" }));
  };

  const filteredProcedimientos = procedimientos.filter(p => 
    p.name?.toLowerCase().includes(buscar.toLowerCase()) || 
    p.sku?.toLowerCase().includes(buscar.toLowerCase())
  );

  // Lógica de Paginación
  const totalItems = filteredProcedimientos.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentItems = filteredProcedimientos.slice(startIndex, startIndex + itemsPerPage);

  useEffect(() => {
    setCurrentPage(1);
  }, [buscar, itemsPerPage]);

  const opcionesUnidades = unidadesMedida.map(u => ({ value: u.id.toString(), label: `${u.codigo} | ${u.name}` }));
  const opcionesItems = tiposItem.map(t => ({ value: t.id.toString(), label: `${t.codigo} | ${t.name}` }));
  const opcionesTributo = tributos.map(t => ({ value: t.id.toString(), label: `${t.codigo} | ${t.name}` }));
  const opcionesEstado = [
    { value: "1", label: "Activo" },
    { value: "0", label: "Inactivo" }
  ];

  if (loading && procedimientos.length === 0) return <Loader />;


  return (
    <div className="p-3 sm:p-6 max-w-7xl mx-auto">
      
      {/* HEADER */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4 sm:mb-6 gap-3">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-slate-800">Procedimientos Odontológicos</h2>
          <p className="text-xs sm:text-sm text-slate-500 mt-1">Catálogo de tratamientos y procedimientos clínicos</p>
        </div>
        <button 
          onClick={() => handleOpenModal()}
          className="w-full sm:w-auto bg-blue-600 text-white px-4 py-2.5 rounded-lg hover:bg-blue-700 transition-colors shadow-sm shadow-blue-200 font-medium flex items-center justify-center gap-2"
        >
          <FiPlus size={20} /> Nuevo Procedimiento
        </button>
      </div>

      {/* Filtros */}
      <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-3 mb-4 sm:mb-6 bg-white p-3 sm:p-4 rounded-xl shadow-sm border border-slate-200">
        <div className="relative flex-grow">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <FiSearch className="h-5 w-5 text-slate-400" />
          </div>
          <input 
            type="text"
            placeholder="Buscar procedimiento por SKU, nombre..."
            value={buscar}
            onChange={(e) => setBuscar(e.target.value)}
            className="pl-10 pr-4 py-2 w-full border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors bg-slate-50 focus:bg-white text-sm"
          />
        </div>

        <div className="flex items-center gap-2">
          <span className="text-sm text-slate-500 font-medium whitespace-nowrap">Mostrar:</span>
          <select
            value={itemsPerPage}
            onChange={(e) => setItemsPerPage(Number(e.target.value))}
            className="border border-slate-300 rounded-lg px-3 py-2 bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-sm"
          >
            {[10, 15, 25, 50, 100].map(num => (
              <option key={num} value={num}>
                {num} registros
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden relative">
        <div className="overflow-x-auto custom-scrollbar">
          <table className="w-full text-sm text-left">
            <thead className="text-xs text-slate-500 bg-slate-50 uppercase border-b border-slate-200">
              <tr>
                <th className="px-4 py-3 font-semibold">SKU</th>
                <th className="px-4 py-3 font-semibold">Nombre</th>
                <th className="px-4 py-3 font-semibold text-center">Duración</th>
                <th className="px-4 py-3 font-semibold text-center">Precio</th>
                <th className="px-4 py-3 font-semibold text-center">Estado</th>
                <th className="px-4 py-3 font-semibold text-center sticky right-0 bg-slate-100 border-l border-slate-200 z-10 shadow-[-4px_0_6px_-2px_rgba(0,0,0,0.05)]">Opciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {currentItems.map((p) => (
                <tr key={p.id} className="hover:bg-blue-50/50 transition-colors group">
                  <td className="px-4 py-3 text-slate-600 font-mono text-xs">{p.sku}</td>
                  <td className="px-4 py-3">
                    <div className="flex flex-col">
                      <span className="text-slate-700 font-medium">{p.name}</span>
                      <span className="text-[10px] text-slate-400 truncate max-w-[200px]">{p.description}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-center text-slate-600 whitespace-nowrap">{p.duracion_minutos} min</td>
                  <td className="px-4 py-3 text-center text-blue-600 font-semibold whitespace-nowrap">${parseFloat(p.price).toFixed(2)}</td>
                  <td className="px-4 py-3 text-center">
                    <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider border ${String(p.status) === "1" ? 'bg-emerald-50 text-emerald-600 border-emerald-200' : 'bg-rose-50 text-rose-600 border-rose-200'}`}>
                      {String(p.status) === "1" ? "Activo" : "Inactivo"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center sticky right-0 bg-slate-100 group-hover:bg-slate-200 border-l border-slate-200 z-0 transition-colors">
                    <button 
                      onClick={() => handleOpenModal(p)}
                      className="bg-white border border-slate-200 text-slate-600 px-3 py-1.5 rounded-lg hover:bg-slate-50 hover:text-blue-600 transition-colors text-xs font-medium"
                    >
                      <FiEdit3 className="inline mr-1" /> Editar
                    </button>
                  </td>
                </tr>
              ))}
              {currentItems.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-10 text-center text-slate-400 italic">No se encontraron procedimientos.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Paginación — Estilo Insumos.jsx */}
      {totalItems > 0 && (
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mt-4 sm:mt-6 px-1 gap-3">
          <p className="text-sm text-slate-500">
            Página <span className="font-medium text-slate-700">{currentPage}</span> de <span className="font-medium text-slate-700">{totalPages}</span>
          </p>
          <div className="flex gap-1 bg-white p-1 rounded-lg border border-slate-200 overflow-x-auto justify-center">
            <button
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${currentPage === 1 ? "text-slate-400 cursor-not-allowed" : "text-slate-600 hover:bg-slate-100"}`}
            >
              ← Ant
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((num) => (
              <button
                key={num}
                onClick={() => setCurrentPage(num)}
                className={`min-w-[32px] px-2 py-1.5 rounded-md text-sm font-medium transition-colors ${currentPage === num ? "bg-blue-50 text-blue-600 font-bold" : "text-slate-600 hover:bg-slate-100"}`}
              >
                {num}
              </button>
            ))}
            <button
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${currentPage === totalPages ? "text-slate-400 cursor-not-allowed" : "text-slate-600 hover:bg-slate-100"}`}
            >
              Sig →
            </button>
          </div>
        </div>
      )}

      {/* MODAL PRINCIPAL */}
      {showModal && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex justify-center items-start sm:items-center z-[100] p-2 sm:p-6 overflow-y-auto font-sans">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-4xl flex flex-col my-4 sm:my-8 animate-fade-in-up">
            <div className="flex items-center justify-between p-4 sm:p-6 border-b border-slate-100">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center text-blue-600 shrink-0">
                  <FiTag size={20} />
                </div>
                <h3 className="text-xl font-bold text-slate-800">
                  {editMode ? "Actualizar Procedimiento" : "Registrar Nuevo Procedimiento"}
                </h3>
              </div>
              <button onClick={() => setShowModal(false)} className="text-slate-400 hover:text-slate-600 p-1">
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-4 sm:p-6 overflow-y-auto custom-scrollbar max-h-[65vh]">
              <form onSubmit={handleSubmit} id="procedimiento-form" className="space-y-6">
                 <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    <div>
                        <label className="block mb-2 text-sm font-semibold text-slate-700">SKU / Código</label>
                        <input 
                          type="text" 
                          name="sku"
                          value={form.sku}
                          onChange={handleChange}
                          className="w-full border border-slate-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-blue-500 text-sm"
                          required
                        />
                    </div>
                    <div>
                        <label className="block mb-2 text-sm font-semibold text-slate-700">Nombre del Procedimiento</label>
                        <input 
                          type="text" 
                          name="name"
                          value={form.name}
                          onChange={handleChange}
                          className="w-full border border-slate-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-blue-500 text-sm"
                          required
                        />
                    </div>
                    <div>
                        <label className="block mb-2 text-sm font-semibold text-slate-700">Precio ($)</label>
                        <input 
                          type="number" 
                          step="0.01"
                          name="price"
                          value={form.price}
                          onChange={handleChange}
                          className="w-full border border-slate-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-blue-500 text-sm"
                          required
                        />
                    </div>
                    <div>
                        <label className="block mb-2 text-sm font-semibold text-slate-700">Duración (Minutos)</label>
                        <input 
                          type="number" 
                          name="duracion_minutos"
                          value={form.duracion_minutos}
                          onChange={handleChange}
                          className="w-full border border-slate-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-blue-500 text-sm"
                          required
                        />
                    </div>
                    <div>
                        <label className="block mb-2 text-sm font-semibold text-slate-700">Unidad de Medida</label>
                        <ReactSelect 
                          options={opcionesUnidades}
                          value={opcionesUnidades.find(o => o.value == form.id_unidad_medida) || null}
                          onChange={(val) => handleSelectChange(val, "id_unidad_medida")}
                          placeholder="Seleccione..."
                          className="text-sm"
                          menuPortalTarget={document.body}
                          styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                        />
                    </div>
                    <div>
                        <label className="block mb-2 text-sm font-semibold text-slate-700">Tipo de Ítem</label>
                        <ReactSelect 
                          options={opcionesItems}
                          value={opcionesItems.find(o => o.value == form.id_type_item) || null}
                          onChange={(val) => handleSelectChange(val, "id_type_item")}
                          placeholder="Seleccione..."
                          className="text-sm"
                          menuPortalTarget={document.body}
                          styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                        />
                    </div>
                    <div>
                        <label className="block mb-2 text-sm font-semibold text-slate-700">Impuesto (Tributo)</label>
                        <ReactSelect 
                          options={opcionesTributo}
                          value={opcionesTributo.find(o => o.value == form.id_tributo) || null}
                          onChange={(val) => handleSelectChange(val, "id_tributo")}
                          placeholder="Seleccione..."
                          className="text-sm"
                          menuPortalTarget={document.body}
                          styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                        />
                    </div>
                    <div>
                        <label className="block mb-2 text-sm font-semibold text-slate-700">Estado</label>
                        <ReactSelect 
                          options={opcionesEstado}
                          value={opcionesEstado.find(o => String(o.value) === String(form.status)) || null}
                          onChange={(val) => handleSelectChange(val, "status")}
                          className="text-sm"
                          menuPortalTarget={document.body}
                          styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                        />
                    </div>
                    <div className="col-span-full">
                        <label className="block mb-2 text-sm font-semibold text-slate-700">Descripción del Tratamiento</label>
                        <textarea 
                          name="description"
                          value={form.description}
                          onChange={handleChange}
                          rows="2"
                          className="w-full border border-slate-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-blue-500 text-sm"
                        ></textarea>
                    </div>
                 </div>
              </form>
            </div>

            <div className="p-4 sm:p-6 border-t border-slate-100 bg-slate-50 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-end gap-3 rounded-b-2xl">
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="w-full sm:w-auto px-5 py-2.5 rounded-lg border border-slate-300 text-slate-700 font-medium hover:bg-white text-sm"
                disabled={submitting}
              >
                Cancelar
              </button>
              <button
                type="submit"
                form="procedimiento-form"
                disabled={submitting}
                className="w-full sm:w-auto px-6 py-2.5 rounded-lg bg-blue-600 text-white font-medium hover:bg-blue-700 text-sm flex items-center justify-center gap-2"
              >
                {submitting && <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>}
                {editMode ? "Actualizar Procedimiento" : "Guardar Procedimiento"}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default ProcedimientosOdontologia;
